import {
  createTheme,
  type CSSVariablesResolver,
  type MantineColorsTuple,
} from "@mantine/core";

// Brand color palette - exported for the handful of call sites that need the raw
// hex in JS (e.g. inline canvas/SVG fills). For everything else prefer the Mantine
// color prop (`color="teal"`) or the `var(--brand-*)` CSS variables.
export const brandColors = {
  black: "#101820",
  teal: "#36AFC9",
  limeGreen: "#A2D55E",
  burntOrange: "#DE854E",
};

// Proper 10-shade ramps (light -> dark) for each brand color.
//
// The solid brand color is anchored at INDEX 6 on purpose: index 6 is the shade
// Mantine's default variant resolver uses for `color="x"` (filled), `c="x"` (text),
// `variant="light|outline|subtle"`, and — since `primaryColor: "teal"` — every
// default-primary element (buttons, checkboxes, focus rings, tabs). Anchoring the
// solid hex there means `color="teal" | "limeGreen" | "burntOrange"` render the
// TRUE brand color instead of the old opacity-stacked value (which resolved to the
// brand color at 60% opacity). Indices 7/8 pin the documented hover/active states.
const teal: MantineColorsTuple = [
  "#e8f7fa",
  "#d1eff5",
  "#a8e0ec",
  "#7fd0e0",
  "#5bc4d9",
  "#45b9d1",
  "#36afc9", // 6 - brand teal
  "#2b9bb3", // 7 - hover
  "#1f8a9e", // 8 - active
  "#14606f",
];

const limeGreen: MantineColorsTuple = [
  "#f2f9e7",
  "#e6f3cf",
  "#d3e9ab",
  "#bfe087",
  "#b0da70",
  "#a9d867",
  "#a2d55e", // 6 - brand lime green
  "#8bc44a", // 7 - hover
  "#74b33a", // 8 - active
  "#5c8f2e",
];

const burntOrange: MantineColorsTuple = [
  "#fcf1ea",
  "#f7ddcb",
  "#f0c3a3",
  "#e9a97b",
  "#e29a63",
  "#de8f56",
  "#de854e", // 6 - brand burnt orange
  "#d17a42", // 7 - hover
  "#c46f36", // 8 - active
  "#a85c28",
];

const black: MantineColorsTuple = [
  "#eceef0",
  "#d5d8db",
  "#aeb3b8",
  "#868d94",
  "#5f6870",
  "#3c454e",
  "#101820", // 6 - brand black
  "#0d141b",
  "#0a1016",
  "#060a0e",
];

// Standard styling every input control shares (see docs/DESIGN_SYSTEM.md).
const INPUT_RADIUS = "md"; // 8px == --radius-md, the design-system input radius
const inputDefaults = { size: "md", radius: INPUT_RADIUS } as const;
// 16px font on the control prevents iOS from zooming on focus (accessibility).
const inputStyles = { input: { fontSize: "1rem" } } as const;

export const brandTheme = createTheme({
  primaryColor: "teal",
  colors: {
    teal,
    limeGreen,
    burntOrange,
    black,
  },
  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  fontFamilyMonospace: "Monaco, Courier, monospace",
  headings: {
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    fontWeight: "500",
  },
  components: {
    Alert: {
      styles: {
        title: {
          fontWeight: "500",
        },
      },
    },
    Text: {
      defaultProps: {
        size: "md",
      },
    },
    Button: {
      defaultProps: {
        color: "var(--brand-teal)",
        size: "md",
      },
      styles: {
        root: {
          fontSize: "1rem", // 16px for accessibility
        },
      },
    },
    // Input.Wrapper is the shared wrapper for every input control, so styling its
    // label / error / required asterisk here normalizes the label treatment across
    // TextInput, Select, NumberInput, MultiSelect, etc. in one place — no per-call
    // `styles={{ label }}` objects needed. (Promoted from the old commission-modal
    // `commissionFieldStyles`.) Error text/border + asterisk color come from
    // `--mantine-color-error`, which index.css points at `--color-danger`.
    InputWrapper: {
      styles: {
        label: {
          fontSize: "14px",
          fontWeight: 500,
          color: "var(--text-secondary)",
          marginBottom: "4px",
        },
        required: {
          color: "var(--color-danger)",
        },
        error: {
          fontSize: "12px",
        },
      },
    },
    TextInput: {
      defaultProps: {
        required: false,
        ...inputDefaults,
      },
      styles: inputStyles,
    },
    InputBase: {
      defaultProps: inputDefaults,
      styles: inputStyles,
    },
    NumberInput: {
      defaultProps: inputDefaults,
      styles: inputStyles,
    },
    Select: {
      defaultProps: inputDefaults,
      styles: inputStyles,
    },
    Textarea: {
      defaultProps: inputDefaults,
      styles: inputStyles,
    },
    MultiSelect: {
      defaultProps: inputDefaults,
      styles: inputStyles,
    },
    Autocomplete: {
      defaultProps: inputDefaults,
      styles: inputStyles,
    },
    TagsInput: {
      defaultProps: inputDefaults,
      styles: inputStyles,
    },
    PasswordInput: {
      defaultProps: inputDefaults,
      styles: inputStyles,
    },
    TimeInput: {
      defaultProps: inputDefaults,
      styles: inputStyles,
    },
    DateInput: {
      defaultProps: {
        ...inputDefaults,
        valueFormat: "MMM D, YYYY",
      },
      styles: {
        input: {
          fontSize: "1rem", // 16px for accessibility
          "&:focus": {
            borderColor: "var(--brand-teal)",
          },
        },
      },
    },
    DatePickerInput: {
      defaultProps: {
        ...inputDefaults,
        valueFormat: "MMM D, YYYY",
      },
      styles: {
        input: {
          fontSize: "1rem", // 16px for accessibility
          "&:focus": {
            borderColor: "var(--brand-teal)",
          },
        },
      },
    },
    Checkbox: {
      styles: {
        body: {
          alignItems: "center",
        },
      },
    },
    SegmentedControl: {
      defaultProps: {
        color: "var(--brand-teal)",
      },
    },
    Avatar: {
      defaultProps: {
        color: "var(--brand-teal)",
      },
    },
    Loader: {
      defaultProps: {
        color: "var(--brand-teal)",
      },
    },
    Table: {
      defaultProps: {
        verticalSpacing: "xs",
        horizontalSpacing: "xs",
      },
    },
    Drawer: {
      styles: {
        title: {
          fontSize: "1.375rem",
          fontWeight: "500",
          lineHeight: "1.4",
        },
      },
    },
  },
  other: {
    brandColors,
  },
});

// Bridge Mantine's built-in semantic channels to our tokens, done via the resolver
// (rather than plain :root rules in index.css) so they reliably override Mantine's
// defaults regardless of stylesheet load order:
//  - `error`  -> --color-danger, so every input's error text/border + required
//    asterisk share one on-brand red.
//  - `dimmed` -> --text-muted, so `c="dimmed"` (the app's muted-text convention)
//    resolves to the one ink-scale muted grey everywhere.
export const brandCssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    "--mantine-color-error": "var(--color-danger, #e8635a)",
    "--mantine-color-dimmed": "var(--text-muted, #8a8a8a)",
  },
  light: {},
  dark: {},
});
